import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { MessageCircle, User as UserIcon, Bot, Search, CheckCircle2, Bell, TrendingUp, TrendingDown, Minus, Star, X, ArrowRightCircle, ExternalLink, StickyNote, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { getChatConversionStats, type ChatPeriodStats } from "@/lib/chat-stats.functions";
import { setChatLeadStatus, setChatConversationNotes, deleteChatConversation } from "@/lib/chat-lead.functions";
import { ConvertChatToLeadDialog } from "@/components/admin/ConvertChatToLeadDialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  c: fallback(z.string().uuid().optional(), undefined).default(undefined),
  f: fallback(z.enum(["all", "converted"]), "all").default("all"),
});

type SearchState = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/chat")({
  validateSearch: zodValidator(searchSchema),
  component: AdminChat,
});

type Conversation = {
  id: string;
  visitor_id: string;
  created_at: string;
  updated_at: string;
  lead_status: "none" | "booking" | "notify" | "manual" | "converted";
  converted_message_id: string | null;
  notes: string | null;
};

type ConversionKind = "booking" | "notify" | "manual" | "converted";

type ChatMessage = {
  id: string;
  conversation_id: string;
  role: string;
  content: string;
  created_at: string;
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "lige nu";
  if (minutes < 60) return `${minutes} min siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} t siden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} d siden`;
  return new Date(iso).toLocaleDateString("da-DK");
}

function AdminChat() {
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/chat" });

  const { data: conversations, isLoading: loadingConvos } = useQuery({
    queryKey: ["admin-chat-conversations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_conversations")
        .select("id, visitor_id, created_at, updated_at, lead_status, converted_message_id, notes")
        .order("updated_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data || []) as Conversation[];
    },
  });

  // Fetch a preview (first user message) per conversation in one query
  // and detect bookings/notify-conversions via assistant emoji markers.
  // Manual leads are tracked separately via chat_conversations.lead_status.
  const { data: convoMeta } = useQuery({
    queryKey: ["admin-chat-meta", conversations?.map((c) => c.id).join(",")],
    queryFn: async () => {
      const empty = { previews: {} as Record<string, string>, autoConversions: {} as Record<string, "booking" | "notify"> };
      if (!conversations || conversations.length === 0) return empty;
      const ids = conversations.map((c) => c.id);
      const { data, error } = await supabase
        .from("chat_messages")
        .select("conversation_id, content, role, created_at")
        .in("conversation_id", ids)
        .order("created_at", { ascending: true });
      if (error) throw error;
      const previews: Record<string, string> = {};
      const autoConversions: Record<string, "booking" | "notify"> = {};
      for (const row of data || []) {
        if (row.role === "user" && !previews[row.conversation_id]) {
          previews[row.conversation_id] = row.content;
        }
        if (row.role === "assistant" && !autoConversions[row.conversation_id]) {
          if (row.content.startsWith("✅") && row.content.includes("prøvekørsel")) {
            autoConversions[row.conversation_id] = "booking";
          } else if (row.content.startsWith("🔔")) {
            autoConversions[row.conversation_id] = "notify";
          }
        }
      }
      return { previews, autoConversions };
    },
    enabled: !!conversations && conversations.length > 0,
  });

  const previews = convoMeta?.previews;
  const autoConversions = convoMeta?.autoConversions;

  // Build merged conversion map: auto markers take precedence; otherwise check manual lead_status.
  const conversions = useMemo(() => {
    const result: Record<string, ConversionKind> = {};
    for (const c of conversations ?? []) {
      // 'converted' (oprettet pipeline-lead) har højeste prioritet.
      if (c.lead_status === "converted" || c.converted_message_id) {
        result[c.id] = "converted";
        continue;
      }
      const auto = autoConversions?.[c.id];
      if (auto) result[c.id] = auto;
      else if (c.lead_status === "manual") result[c.id] = "manual";
    }
    return result;
  }, [conversations, autoConversions]);

  const filteredConvos = useMemo(() => {
    const list = conversations || [];
    const q = urlSearch.q.trim().toLowerCase();
    const onlyConverted = urlSearch.f === "converted";
    return list.filter((c) => {
      if (onlyConverted && !conversions[c.id]) return false;
      if (!q) return true;
      const preview = previews?.[c.id]?.toLowerCase() || "";
      return (
        c.visitor_id.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        preview.includes(q)
      );
    });
  }, [conversations, previews, conversions, urlSearch.q, urlSearch.f]);

  // Auto-select first conversation when none chosen
  useEffect(() => {
    if (!urlSearch.c && filteredConvos.length > 0) {
      navigate({
        search: (prev: SearchState) => ({ ...prev, c: filteredConvos[0].id }),
        replace: true,
      });
    }
  }, [urlSearch.c, filteredConvos, navigate]);

  const selectedId = urlSearch.c;

  const { data: messages, isLoading: loadingMessages } = useQuery({
    queryKey: ["admin-chat-messages", selectedId],
    queryFn: async () => {
      if (!selectedId) return [] as ChatMessage[];
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", selectedId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as ChatMessage[];
    },
    enabled: !!selectedId,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const setQ = (q: string) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, q }), replace: true });
  };
  const setFilter = (f: "all" | "converted") => {
    navigate({ search: (prev: SearchState) => ({ ...prev, f }), replace: true });
  };
  const selectConvo = (id: string) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, c: id }), replace: true });
  };

  const convertedCount = useMemo(
    () => (conversations ?? []).filter((c) => conversions[c.id]).length,
    [conversations, conversions],
  );

  const selectedConvo = filteredConvos.find((c) => c.id === selectedId) ?? conversations?.find((c) => c.id === selectedId);

  // Mutation for marking conversations as manual leads
  const queryClient = useQueryClient();
  const setLeadFn = useServerFn(setChatLeadStatus);
  const markLeadMutation = useMutation({
    mutationFn: (input: { conversationId: string; status: "manual" | "none" }) =>
      setLeadFn({ data: input }),
    onSuccess: (_data, vars) => {
      toast.success(vars.status === "manual" ? "Markeret som lead" : "Markering fjernet");
      queryClient.invalidateQueries({ queryKey: ["admin-chat-conversations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-chat-conversion-stats"] });
    },
    onError: (err: Error) => toast.error(`Kunne ikke opdatere: ${err.message}`),
  });

  // Dialog til at konvertere chat-samtale til pipeline-lead.
  const [convertOpen, setConvertOpen] = useState(false);

  // Notes-editor state pr. valgte samtale (lokalt buffer + dirty flag).
  const setNotesFn = useServerFn(setChatConversationNotes);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);

  useEffect(() => {
    setNotesDraft(selectedConvo?.notes ?? "");
    setNotesDirty(false);
  }, [selectedId, selectedConvo?.notes]);

  const saveNotesMutation = useMutation({
    mutationFn: (input: { conversationId: string; notes: string | null }) =>
      setNotesFn({ data: input }),
    onSuccess: () => {
      toast.success("Note gemt");
      setNotesDirty(false);
      queryClient.invalidateQueries({ queryKey: ["admin-chat-conversations"] });
    },
    onError: (err: Error) => toast.error(`Kunne ikke gemme note: ${err.message}`),
  });

  // Slet en samtale (manuel oprydning). Bekræftelses-dialog vises før kald,
  // så et fejlklik på papirkurven ikke koster historik. Efter sletning
  // ryddes ?c=… fra URL'en så detail-panelet falder tilbage til auto-valg
  // af den nyeste resterende samtale.
  const deleteChatFn = useServerFn(deleteChatConversation);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; preview: string } | null>(null);
  const deleteChatMutation = useMutation({
    mutationFn: (input: { conversationId: string }) =>
      deleteChatFn({ data: input }),
    onSuccess: (_data, vars) => {
      toast.success("Samtale slettet");
      setDeleteTarget(null);
      // Hvis vi slettede den aktuelt valgte samtale, fjern ?c så
      // auto-select kan vælge en ny.
      if (urlSearch.c === vars.conversationId) {
        navigate({
          search: (prev: SearchState) => ({ ...prev, c: undefined }),
          replace: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["admin-chat-conversations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-chat-conversion-stats"] });
    },
    onError: (err: Error) => toast.error(`Kunne ikke slette: ${err.message}`),
  });
  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground">Chat-samtaler</h1>
      <p className="mt-1 text-sm text-muted-foreground">Log over alle samtaler med AI-assistenten</p>

      <ConversionRateWidget />

      <div className="mt-6 grid gap-4 lg:grid-cols-[360px_1fr]">
        {/* Conversation list */}
        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={urlSearch.q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Søg samtale..."
                className="pl-9"
              />
            </div>
            <div className="mt-2 flex items-center gap-1 rounded-lg bg-muted p-0.5">
              <button
                type="button"
                onClick={() => setFilter("all")}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  urlSearch.f === "all"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Alle ({conversations?.length ?? 0})
              </button>
              <button
                type="button"
                onClick={() => setFilter("converted")}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  urlSearch.f === "converted"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Kun konverterede ({convertedCount})
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {loadingConvos ? "Indlæser..." : `${filteredConvos.length} samtaler`}
            </p>
          </div>
          <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
            {loadingConvos ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Indlæser...</div>
            ) : filteredConvos.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Ingen samtaler endnu</div>
            ) : (
              <ul>
                {filteredConvos.map((convo) => {
                  const preview = previews?.[convo.id] || "Ingen besked endnu";
                  const active = convo.id === selectedId;
                  const conversion = conversions?.[convo.id];
                  return (
                    <li key={convo.id} className="group relative">
                      <button
                        onClick={() => selectConvo(convo.id)}
                        className={`flex w-full flex-col gap-1 border-b border-border px-4 py-3 pr-10 text-left transition-colors ${
                          active ? "bg-accent/10" : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="truncate text-xs font-mono text-muted-foreground">
                              {convo.visitor_id.slice(0, 8)}…
                            </span>
                            {conversion === "booking" && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success">
                                <CheckCircle2 size={10} />
                                Booking
                              </span>
                            )}
                            {conversion === "notify" && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                                <Bell size={10} />
                                Notify
                              </span>
                            )}
                            {conversion === "manual" && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                <Star size={10} />
                                Manuel
                              </span>
                            )}
                            {conversion === "converted" && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success">
                                <ArrowRightCircle size={10} />
                                Lead
                              </span>
                            )}
                            {convo.notes && convo.notes.trim().length > 0 && (
                              <span
                                title={convo.notes}
                                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                              >
                                <StickyNote size={10} />
                                Note
                              </span>
                            )}
                          </div>
                          <span className="shrink-0 text-[10px] text-muted-foreground">
                            {formatRelative(convo.updated_at)}
                          </span>
                        </div>
                        <p className="line-clamp-2 text-sm text-foreground">{preview}</p>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ id: convo.id, preview });
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground opacity-0 transition hover:bg-destructive/10 hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                        aria-label="Slet samtale"
                        title="Slet samtale"
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Conversation detail */}
        <div className="rounded-xl border border-border bg-card">
          {!selectedId ? (
            <div className="flex h-[400px] items-center justify-center text-sm text-muted-foreground">
              <MessageCircle className="mr-2" size={18} /> Vælg en samtale
            </div>
          ) : (
            <div className="flex h-[calc(100vh-220px)] flex-col">
              <div className="border-b border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-foreground">Samtale</div>
                    {selectedId && conversions?.[selectedId] === "booking" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
                        <CheckCircle2 size={12} />
                        Konverterede til booking
                      </span>
                    )}
                    {selectedId && conversions?.[selectedId] === "notify" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
                        <Bell size={12} />
                        Notify-forespørgsel
                      </span>
                    )}
                    {selectedId && conversions?.[selectedId] === "manual" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                        <Star size={12} />
                        Manuelt markeret som lead
                      </span>
                    )}
                    {selectedId && conversions?.[selectedId] === "converted" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
                        <ArrowRightCircle size={12} />
                        Konverteret til pipeline-lead
                      </span>
                    )}
                  </div>
                  {selectedId && selectedConvo && (
                    <div className="flex items-center gap-2">
                      {selectedConvo.converted_message_id ? (
                        <Button asChild size="sm" variant="outline">
                          <Link
                            to="/admin/beskeder"
                            search={{ selected: selectedConvo.converted_message_id } as never}
                          >
                            <ExternalLink size={14} className="mr-1" />
                            Åbn lead
                          </Link>
                        </Button>
                      ) : (
                        <>
                          {selectedConvo.lead_status === "manual" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={markLeadMutation.isPending}
                              onClick={() =>
                                markLeadMutation.mutate({ conversationId: selectedId, status: "none" })
                              }
                            >
                              <X size={14} className="mr-1" />
                              Fjern markering
                            </Button>
                          )}
                          {selectedConvo.lead_status !== "manual" &&
                            !conversions?.[selectedId] && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={markLeadMutation.isPending}
                                onClick={() =>
                                  markLeadMutation.mutate({
                                    conversationId: selectedId,
                                    status: "manual",
                                  })
                                }
                              >
                                <Star size={14} className="mr-1" />
                                Markér som lead
                              </Button>
                            )}
                          <Button size="sm" onClick={() => setConvertOpen(true)}>
                            <ArrowRightCircle size={14} className="mr-1" />
                            Konverter til lead
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() =>
                          setDeleteTarget({
                            id: selectedId,
                            preview: previews?.[selectedId] ?? "",
                          })
                        }
                        disabled={deleteChatMutation.isPending}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Slet samtale
                      </Button>
                    </div>
                  )}
                </div>
                {selectedConvo && (
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      Besøgende: <code className="font-mono">{selectedConvo.visitor_id.slice(0, 12)}…</code>
                    </span>
                    <span>Startet: {formatTime(selectedConvo.created_at)}</span>
                    <span>Sidste aktivitet: {formatTime(selectedConvo.updated_at)}</span>
                  </div>
                )}
              </div>
              {selectedId && (
                <div className="border-b border-border bg-muted/20 p-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <label
                      htmlFor="chat-notes"
                      className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      Intern note
                    </label>
                    {notesDirty && (
                      <span className="text-[10px] text-muted-foreground">Ikke gemt</span>
                    )}
                  </div>
                  <Textarea
                    id="chat-notes"
                    value={notesDraft}
                    onChange={(e) => {
                      setNotesDraft(e.target.value);
                      setNotesDirty(e.target.value !== (selectedConvo?.notes ?? ""));
                    }}
                    placeholder="Fx: ringede 14:30, vil prøvekøre Tesla Model 3"
                    rows={2}
                    maxLength={2000}
                    className="resize-y bg-background text-sm"
                  />
                  <div className="mt-1.5 flex items-center justify-end gap-2">
                    {notesDirty && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setNotesDraft(selectedConvo?.notes ?? "");
                          setNotesDirty(false);
                        }}
                        disabled={saveNotesMutation.isPending}
                      >
                        Annuller
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() =>
                        saveNotesMutation.mutate({
                          conversationId: selectedId,
                          notes: notesDraft.trim() ? notesDraft : null,
                        })
                      }
                      disabled={!notesDirty || saveNotesMutation.isPending}
                    >
                      {saveNotesMutation.isPending ? "Gemmer…" : "Gem note"}
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {loadingMessages ? (
                  <div className="text-center text-sm text-muted-foreground">Indlæser beskeder...</div>
                ) : messages && messages.length > 0 ? (
                  messages.map((m) => (
                    <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
                      {m.role === "assistant" && (
                        <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                          <Bot size={14} />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                          m.role === "user"
                            ? "bg-accent text-accent-foreground"
                            : "bg-muted text-foreground"
                        }`}
                      >
                        <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-ol:my-1">
                          <ReactMarkdown>{m.content}</ReactMarkdown>
                        </div>
                        <div
                          className={`mt-1 text-[10px] ${
                            m.role === "user" ? "text-accent-foreground/70" : "text-muted-foreground"
                          }`}
                        >
                          {formatTime(m.created_at)}
                        </div>
                      </div>
                      {m.role === "user" && (
                        <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <UserIcon size={14} />
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-sm text-muted-foreground">Ingen beskeder</div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
        </div>
      </div>

      <ConvertChatToLeadDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        conversationId={selectedId ?? null}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(v) => {
          if (!v && !deleteChatMutation.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet chat-samtalen permanent?</AlertDialogTitle>
            <AlertDialogDescription>
              Hele samtalen og alle dens beskeder slettes for altid og kan ikke
              gendannes. Eventuelle pipeline-leads, der allerede er konverteret
              fra denne samtale, bevares uændret.
              {deleteTarget?.preview && (
                <span className="mt-3 block rounded-md bg-muted p-2 text-xs italic text-foreground">
                  „{deleteTarget.preview.slice(0, 200)}
                  {deleteTarget.preview.length > 200 ? "…" : ""}"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteChatMutation.isPending}>
              Annuller
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteChatMutation.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteChatMutation.mutate({ conversationId: deleteTarget.id });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteChatMutation.isPending ? "Sletter…" : "Slet samtale"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConversionRateWidget() {
  const fetchStats = useServerFn(getChatConversionStats);
  const [activeDays, setActiveDays] = useState<7 | 30 | 90>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-chat-conversion-stats"],
    queryFn: () => fetchStats(),
    staleTime: 60_000,
  });

  const active: ChatPeriodStats | undefined = data?.periods.find((p) => p.days === activeDays);
  const ratePct = active ? (active.conversionRate * 100).toFixed(1) : "—";

  // Beregn trend (procentpoint-forskel) ift. foregående tilsvarende periode.
  // Vi viser kun trend hvis foregående periode rent faktisk havde samtaler,
  // ellers er sammenligningen meningsløs.
  const trendDelta =
    active && active.previousConversionRate !== null
      ? (active.conversionRate - active.previousConversionRate) * 100
      : null;
  // Kun "flat" hvis forskel er under et halvt procentpoint.
  const trendDirection: "up" | "down" | "flat" | null =
    trendDelta === null
      ? null
      : Math.abs(trendDelta) < 0.5
        ? "flat"
        : trendDelta > 0
          ? "up"
          : "down";
  const TrendIcon =
    trendDirection === "up" ? TrendingUp : trendDirection === "down" ? TrendingDown : Minus;
  const trendColor =
    trendDirection === "up"
      ? "text-success"
      : trendDirection === "down"
        ? "text-destructive"
        : "text-muted-foreground";

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/10 p-2 text-primary">
            <TrendingUp size={20} />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Konverteringsrate
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-4xl font-bold tabular-nums text-foreground">
                {isLoading ? "…" : `${ratePct}%`}
              </span>
              {trendDirection && trendDelta !== null && (
                <span
                  className={`inline-flex items-center gap-0.5 text-sm font-medium tabular-nums ${trendColor}`}
                  title={`Foregående ${activeDays} dage: ${(active!.previousConversionRate! * 100).toFixed(1)}% (${active!.previousConversations} samtaler)`}
                >
                  <TrendIcon size={14} />
                  {trendDirection === "flat"
                    ? "0,0"
                    : `${trendDelta > 0 ? "+" : ""}${trendDelta.toFixed(1)}`}
                  pp
                </span>
              )}
              {active && active.conversations > 0 && (
                <span className="text-sm text-muted-foreground">
                  {active.converted} af {active.conversations} samtaler
                </span>
              )}
            </div>
            {active && active.converted > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {active.bookings > 0 && (
                  <span className="inline-flex items-center gap-1 mr-3">
                    <CheckCircle2 size={11} className="text-success" />
                    {active.bookings} prøvekørsler
                  </span>
                )}
                {active.notifies > 0 && (
                  <span className="inline-flex items-center gap-1 mr-3">
                    <Bell size={11} className="text-accent" />
                    {active.notifies} notify-anmodninger
                  </span>
                )}
                {active.manuals > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Star size={11} className="text-primary" />
                    {active.manuals} manuelt markerede
                  </span>
                )}
              </p>
            )}
            {error && (
              <p className="mt-1 text-xs text-destructive">Fejl: {(error as Error).message}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 self-start rounded-lg bg-muted p-0.5">
          {([7, 30, 90] as const).map((days) => {
            const period = data?.periods.find((p) => p.days === days);
            const isActive = activeDays === days;
            return (
              <button
                key={days}
                type="button"
                onClick={() => setActiveDays(days)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span>{days}d</span>
                {period && (
                  <span className={`ml-1.5 tabular-nums ${isActive ? "text-primary" : "text-muted-foreground/70"}`}>
                    {(period.conversionRate * 100).toFixed(0)}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {active && active.dailySeries.length > 1 && (
        <ConversionSparkline
          series={active.dailySeries}
          overallRate={active.conversionRate}
        />
      )}
    </div>
  );
}

function ConversionSparkline({
  series,
  overallRate,
}: {
  series: {
    date: string;
    conversations: number;
    converted: number;
    conversionRate: number | null;
  }[];
  overallRate: number;
}) {
  const width = 600;
  const height = 56;
  const padX = 4;
  const padY = 6;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  // Skala: brug max(faktiske rater, gennemsnit, 5%) så små tal stadig er
  // synlige uden at fylde hele grafen.
  const ratesForScale = series
    .map((p) => p.conversionRate)
    .filter((r): r is number => r !== null);
  const maxRate = Math.max(0.05, overallRate, ...ratesForScale);

  const xFor = (i: number) =>
    padX + (series.length === 1 ? innerW / 2 : (i * innerW) / (series.length - 1));
  const yFor = (rate: number) => padY + innerH - (rate / maxRate) * innerH;

  // Byg path-segmenter med gap på dage uden samtaler (så vi ikke fakerer 0%).
  const segments: string[] = [];
  let current: string[] = [];
  series.forEach((p, i) => {
    if (p.conversionRate === null) {
      if (current.length > 0) {
        segments.push(current.join(" "));
        current = [];
      }
      return;
    }
    const x = xFor(i).toFixed(1);
    const y = yFor(p.conversionRate).toFixed(1);
    current.push(current.length === 0 ? `M ${x} ${y}` : `L ${x} ${y}`);
  });
  if (current.length > 0) segments.push(current.join(" "));

  const conversionDots = series
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => p.converted > 0 && p.conversionRate !== null);

  const overallY = yFor(Math.min(overallRate, maxRate));
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "short" });

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="mb-1.5 flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>Daglig konverteringsrate</span>
        <span className="tabular-nums normal-case">
          {formatDate(series[0].date)} – {formatDate(series[series.length - 1].date)}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="block h-14 w-full"
        role="img"
        aria-label="Daglig konverteringsrate over valgt periode"
      >
        <line
          x1={padX}
          x2={width - padX}
          y1={overallY}
          y2={overallY}
          stroke="hsl(var(--muted-foreground))"
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.4}
        />
        {segments.map((d, idx) => (
          <path
            key={idx}
            d={d}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {conversionDots.map(({ p, i }) => (
          <circle
            key={p.date}
            cx={xFor(i)}
            cy={yFor(p.conversionRate!)}
            r={2}
            fill="hsl(var(--primary))"
          >
            <title>
              {formatDate(p.date)}: {(p.conversionRate! * 100).toFixed(0)}% ({p.converted}/
              {p.conversations})
            </title>
          </circle>
        ))}
      </svg>
      <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>0%</span>
        <span className="tabular-nums">Gennemsnit: {(overallRate * 100).toFixed(1)}%</span>
        <span className="tabular-nums">{(maxRate * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}
